import {
  Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef
} from '@angular/core';
import {
  SegmentDefinition,
  Segment,
  Level,
  Findings,
  SegmentPayload
} from '@ispm-client/modules/segments/interfaces';
import { SegmentService } from '@ispm-client/modules/segments/services/segments.service';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { Widget } from '@ispm/widgets/decorator/widget.decorator';
import { isEmpty, get, set, flattenDeep, cloneDeep, capitalize, concat, differenceWith,isEqual, groupBy, orderBy, filter } from 'lodash';
import { IdynamicFormItem } from '@ispm/dynamic-form/interfaces/IdynamicFormItem';
import { LegendItem } from '@ispm-client/modules/segments/components/legend/legend.component';
import { Observable } from 'rxjs/Observable';
import { ColorCodingDerivation } from '@ispm-client/modules/segments/components/segment-annotator/color-coding-derivation';
import { Subject, ReplaySubject } from 'rxjs';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { BehaviorSubject } from 'rxjs/Rx';
import { connectorObservable,deletedField,componentInitialized,componentDestroyed} from '@ispm-client/modules/segments/interfaces/constants';
import {FieldMappingTransformation} from "@ispm-client/modules/segments/components/segment/segment-field-mapping/field-mapping";
import * as jsonPath from 'jsonpath';
import { AsyncWidget } from '@ispm-client/modules/widgets/decorator/async-widget.decorator';



@Component({
  selector: 'ispm-segment-annotator',
  templateUrl: './segment-annotator.component.html',
  styleUrls: ['./segment-annotator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ColorCodingDerivation,FieldMappingTransformation]
})

@AsyncWidget('annotator')
export class SegmentAnnotatorComponent implements OnInit, IWidgetItem {


  @Input() payload: SegmentPayload;
  @Input() isLegendPresent: boolean = false;
  @Input() isReadOnly: boolean;
  @Input() segmentData: any[] = [];
  @Input() annotatorType: string;
  @Input() isEditable: boolean = true;
  items: LegendItem[];
  loader: boolean = false;
  loaderType : string ;
  widgetUpdateValueCallback: (value) => void;
  widgetLoadDone: ()=>void;
  segmentDefinition: SegmentDefinition;
  segmentsSvg$: Observable<string>;
  segments$: Subject<Segment[]> = new BehaviorSubject<Segment[]>([]);
  segments: Segment[];
  segmentExternal$: Observable<Findings>;
  segmentEmitter$: Subject<any> = new Subject<any>();
  segmentRemoved: any;
  payloadBindingData: any[];
  payloadregistered: boolean = false;
  connectorCounter : number = 0;
  legendPosition: string;
  legendTitle: string;
  priorityBasedHighlight: boolean = false;
  priorityBasedHighlightKeys: Array<string>;
  private destroyer$: Subject<any> = new Subject();
  segmentIdentifier:Array<string>=[];
  maximizable: boolean;

  constructor(private segmentsService: SegmentService,
              private changeDetectorRef: ChangeDetectorRef,
              private colorCodingDerivation: ColorCodingDerivation,
              private fieldMappingTransform:FieldMappingTransformation
              ) {

  }

  set widgetData(widgetData: any) {
    if(this.isReadOnly && isEmpty(widgetData)){
      this.segmentData = [];
      this.updateSegmentsData();
     }
    if (!isEmpty(widgetData) && JSON.stringify(this.segmentData) !== JSON.stringify(widgetData)) {
      this.segmentData = this.filterSegments(widgetData);
      if (this.payloadBindingData) {
        this.segmentData = concat(this.segmentData, this.payloadBindingData);
      }
      this.updateSegmentsData();
    }
  }

  /**
   * This filteration is required incase data is provided if deleted items are provided .
   * @param segmentsData
   */
  filterSegments(segmentsData){
    if(!isEmpty(segmentsData)){
      return segmentsData.filter(dataItem => dataItem[deletedField] !== true);
    }
  }

  set widgetConfig(config: IdynamicFormItem) {
    this.payload = config.widgetPayload;
    this.annotatorType = this.payload.annotatorType;
    this.isReadOnly = config.widgetPayload.disabled || this.payload.readOnly;
    this.isLegendPresent = this.payload.isLegendPresent;
    this.legendTitle = this.payload.legendTitle;
    this.loaderType = this.payload.loaderType;
    if(!config.widgetPayload.isEditable){
      this.isEditable = config.widgetPayload.isEditable;
    }
    this.connectorSubscribtion();
  }

  setSegmentIdentifier(){
    if((this.segmentDefinition,'imageConfig')){
      this.segmentIdentifier = get(this.segmentDefinition.imageConfig,'segmentIdentifier');
    }
  }

  /**
   * This connector subscription is required as when a external component providing data the observers in the component expires,
   * the observers need to be reset and once the external initializes an Intialization command need to be recieved ,
   * so that the observers start subscribing the newly created observers in the segment service
   */
  connectorSubscribtion(){
    this.segmentsService.getObservable(connectorObservable, true).subscribe(value => {
        if (value === componentDestroyed && this.connectorCounter === 0) {
          this.segmentExternal$ = null;
          this.segmentEmitter$ = null;
          this.connectorCounter++;
        }
        if (value === componentInitialized && this.connectorCounter === 1) {
          this.segmentData = [];
          this.ngOnInit();
          this.connectorCounter = 0;
        }
      }
    );
  }

  ngOnInit() {
      this.segmentExternal$ = this.segmentsService.getObservable(this.payload.findingsObservableName, true)
        .filter(segmentExternal => !isEmpty(segmentExternal));
      this.segmentEmitter$ = this.segmentsService.getObservable(this.payload.segmentsObservableName, true);
    this.getSegmentDefinitions();

  }

  /**
   * Get the definitions for the segments of the viewer.
   * Segments are derived based on the cancerType.
   */
  getSegmentDefinitions() {
    if (!isEmpty(this.payload)) {
      this.loader = true;
      this.segmentsService.getSegmentsBasedOnCondition(this.payload)
        .takeUntil(this.destroyer$.asObservable())
        .filter(segmentDefinition => !isEmpty(segmentDefinition))
        .filter(segmentDefinition => this.payload.annotatorType === segmentDefinition.type)
        .subscribe(segmentDefinition => {
            if (!isEmpty(segmentDefinition)) {
              this.loader = false;
              this.prepareSegmentDefinition(segmentDefinition);
            }

          }
        );
    }
  }

  prepareSegmentDefinition(segmentDefs) {
    this.segmentDefinition = {
      segmentConfig: { values: segmentDefs.segmentConfig.values},
      segmentFiles: segmentDefs.segmentConfig.segmentFiles,
      imageConfig: segmentDefs.segmentConfig.imageConfig,
      segmentAnnotations: segmentDefs.segmentConfig.annotations,
      legendCategories: segmentDefs.segmentConfig.legendCategories,
      legendPosition: segmentDefs.segmentConfig.legendPosition,
      priorityBasedHighlight: segmentDefs.segmentConfig.priorityBasedHighlight,
      priorityBasedHighlightKeys: segmentDefs.segmentConfig.priorityBasedHighlightKeys,
      maximizable: segmentDefs.segmentConfig.maximizable
    };
    this.segmentDefinition['segmentAnnotationBindings'] = this.payload.segmentAnnotationBindings;
    this.segmentsService.segmentDef = this.segmentDefinition;
    this.segments = this.segmentDefinition.segmentConfig.values;
    this.segments$.next(this.segments);
    this.fetchSvgContent();
    this.fillLegendItems();
    this.fetchSegmentsData();
    this.fetchExternalData();
    this.updateSegmentsData();
    this.loader = false;
    this.setSegmentIdentifier();
    this.changeDetectorRef.markForCheck();

  }

  /**
   * Legends specific to the type are derived from the segment definition.
   */
  fillLegendItems() {
    if (!isEmpty(this.segmentDefinition)) {
      const legendItem = this.segmentDefinition.legendCategories
        .find(legend => legend.type === this.annotatorType);
      this.items = legendItem.items;
      this.legendPosition =  this.segmentDefinition.legendPosition;
      this.priorityBasedHighlight =  this.segmentDefinition.priorityBasedHighlight;
      this.priorityBasedHighlightKeys = this.segmentDefinition.priorityBasedHighlightKeys;

    }
  }

  /**
   * SVG Content for the Segments Viewer.It is wrapped around a Observable as the child can subscribe
   * and get based on the fetch action is completed.
   */
  private fetchSvgContent() {
    if (this.segments) {
      this.segmentsSvg$ = this.segmentsService.getSvgContent();
    }
  }

  /**
   * Fetch the segment specific data using the databindings defined in the widget Payload.
   * Use only non Configuration bindings which are data specific to context.
   * Here we register to fetch incase payload register is false.
   * Once the data is captured, combine with the data provided from the widget data incase recieved.
   */
  fetchSegmentsData() {
    let payloadData;
    if (this.segmentsService.isNonConfigurationBindings(this.payload) && !this.payloadregistered) {
      this.registerPayloadBindings().filter(data => !isEmpty(data))
        .takeUntil(this.destroyer$.asObservable())
        .subscribe(data => {
        const dataItems: any[] = [];
        const segData = Object.assign({ }, ...data);
        Object.keys(segData).forEach(key => {
          if (!isEmpty(segData[key])) {
          dataItems.push(segData[key]);
          }
        });
        this.payloadBindingData = flattenDeep(dataItems);

        if (!isEmpty(this.payloadBindingData)) {
          if (this.segmentData) {
            this.segmentData = differenceWith(this.segmentData,payloadData,isEqual);
            this.segmentData = concat(this.segmentData, this.payloadBindingData);
          }
          else {
            this.segmentData = cloneDeep(this.payloadBindingData);
          }
          payloadData = this.payloadBindingData;
          this.updateSegmentsData();
        }

      });
    }
  }

  /**
   * Get the external data from any other component to which this component is subscribed.
   * In Current case we get data from findings component .
   */
  fetchExternalData() {
      combineLatest(this.segmentExternal$, this.segmentsSvg$)
        .filter(externalData => !isEmpty(externalData))
        .takeUntil(this.destroyer$.asObservable())
        .subscribe(externalData => {
          this.segmentData = externalData[0].data;
          if (!this.payloadregistered) {
            this.fetchSegmentsData();
          }
          if (!isEmpty(this.payloadBindingData)) {
            this.segmentData = differenceWith(this.segmentData,this.payloadBindingData,isEqual)
            this.segmentData = concat(this.segmentData, this.payloadBindingData);
          }
          this.segmentRemoved = externalData[0].itemDeleted;
          this.updateSegmentsData();

        });
  }

  /**
   * Register the payload databindings and set the payloadregistered as true.
   * @returns {Observable<any>}
   */
  registerPayloadBindings() {
    this.payloadregistered = true;
    return this.segmentsService.getAnnotationDataForSegments(this.payload.segmentAnnotationBindings);
  }

  /**
   * While data is sent back other component , we can filter with only with the
   * data this component has to be emitted to external component.
   * @param segmentData
   * @returns {any}
   */
  filterPayloadBindingData(segmentData) {
    const emitItems = segmentData.filter( segData => {
      if (segData['category'].code === this.annotatorType) {
        return true;
      }
    });
    return emitItems;
  }

  /**
   * This method is invoked when there is a segment selected/deselected.
   * @param {Segment} segment
   */
  updateSegments(segment: Segment) {

    let segmentEmitData:any={};
    let index = 0;
    let structure=[];
    this.setSegmentIdentifier();
    this.segmentDefinition.segmentAnnotations.forEach(segAnn => {
      const location = this.fieldMappingTransform.getModelFieldName(segAnn.fieldMappings);
      if (segAnn.annotator == this.annotatorType) {
        segmentEmitData[location]=this.fieldMappingTransform.transform(segment,segAnn.fieldMappings,this.segmentIdentifier);
        segmentEmitData['category'] = { code: this.annotatorType, display: capitalize(this.annotatorType)};
        segmentEmitData['newItem'] = true;

        if (segment.active === true) {
          segment.annotator = { type: segAnn.annotator, level: { code: ''}};
        }

        if (segment.active === false) {
          this.segmentData.map((segmentItem) => {
            if (this.matchSegmentItem(segmentItem,segment,location,segAnn.fieldMappings)){
              segmentItem[deletedField] = true;
            }

          });
        }
        if (segment.highlight === true) {
          this.segmentData.map((segmentItem) => {
             if(this.matchSegmentItem(segmentItem,segment,location,segAnn.fieldMappings)){
                segmentItem['highlight'] = true;
              }

          });
        }
        if (segment.highlight === true || segment.active === false) {
          this.segmentEmitter$.next(this.filterPayloadBindingData(this.segmentData));
          segment.highlight = false;
          return;
        }
        if (this.segmentData) {
          this.segmentData.unshift(segmentEmitData);
        }
        else {
          this.segmentData = [];
          this.segmentData.push(segmentEmitData);
        }
        this.segmentEmitter$.next(this.filterPayloadBindingData(this.segmentData));


    }});

  }


  /**
   * This method is used for comparision between the segmentItem property and the segment identifier properties
   */
  matchSegmentItem(segmentItem,segment,location,fieldMappings):boolean{
    let baseModelField = this.fieldMappingTransform.getModelFieldName(fieldMappings);
    const mappingObject = this.fieldMappingTransform.segmentToModelMapper(this.segmentDefinition.segmentAnnotations[0]);
    let validateDataArray:Array<boolean> = [];
    this.setSegmentIdentifier();
    const segmentDataArray = segment.name.toString().split("-");
    this.segmentIdentifier.forEach(identifier=>{
      let field = mappingObject[identifier];
      if (this.segmentIdentifier.length > 1) {
        if(segmentDataArray[this.segmentIdentifier.indexOf(identifier)] === this.fieldMappingTransform.getCode(segmentItem,field,this.segmentIdentifier,fieldMappings)){
          validateDataArray.push(true);
        }else{
          validateDataArray.push(false);
        }
      }else{
        if(segment[identifier] === this.fieldMappingTransform.getCode(segmentItem,field,this.segmentIdentifier,fieldMappings)){
          validateDataArray.push(true);
        }else{
          validateDataArray.push(false);
        }
      }

    });
    if(validateDataArray.includes(false)){
      return false;
    }else{
      return true
    }

  }



  resetSegments(segments) {
    const defaultFill = this.segmentDefinition.imageConfig.defaultFill;
    segments.forEach(segment => {
      segment.active = false;
      segment.annotator = null;
      segment.level = { code: '', fillColor: defaultFill};
      //segment.level = { code: '', fillColor: ''};
      segment.disabled = false;
      segment.resected = false;
    });
  }

  disableStoredSegments(segments) {
    let location;
    this.segmentDefinition.segmentAnnotations.forEach(segAnn => {
      if (segAnn.annotator == this.annotatorType) {
        location = this.fieldMappingTransform.getModelFieldName(segAnn.fieldMappings);
      }
    });
    this.segmentData.map(dataItem => {
      if (dataItem['id']) {
        segments.map(segment => {
          if (segment.name === get(dataItem[location], 'code')) {
            segment.disabled = true;
          }
        });
      }
    });
  }

  updateSegmentsData() {

    this.loader = false;
    if (!isEmpty(this.segmentDefinition)) {
      const defaultFill = this.segmentDefinition.imageConfig.defaultFill;
      const data = this.segmentData;
      this.resetSegments(this.segments);
      this.disableStoredSegments(this.segments);
      const seg = cloneDeep(this.segments);
      this.segmentDefinition.segmentAnnotations.forEach(segAnn => {

        if (this.segmentRemoved) {
          if (get(this.segmentRemoved, 'category.code') == segAnn.annotator) {
            seg.forEach(segment => {
              if (this.segmentRemoved && this.checkSegmentRemoved(segment,segAnn.fieldMappings)) {
                segment.active = false;
                segment.level = { code: '', fillColor: ''};
                segment.annotator = null;
                this.segmentRemoved = null;
              }
            });
          }
        }

        let annotationDataSet = !isEmpty(data) ? data.filter(item => get(item, 'category.code') == segAnn.annotator) : [];
        if(this.priorityBasedHighlight) {
          let tempArray =[];
          let groupData = groupBy(annotationDataSet, (item) => {
            let basedOnKeys = ['location.code', 'lungRadsCategoryWithModifierS.code'];
            if(this.priorityBasedHighlightKeys && this.priorityBasedHighlightKeys.length > 0) {
              let tempGroupKey = '';
              for(let key of this.priorityBasedHighlightKeys) {
                tempGroupKey+= get(item, key);
              }
              if(tempGroupKey){
                return tempGroupKey;
              }
            }
           return get(item, `${get(segAnn.fieldMappings,'name')}.code`)
          });

          for (let key in groupData) {
            let tempData = get(groupData, key);
            tempData = orderBy(tempData, [`${get(segAnn.fieldMappings,'level')}.code`], ['desc'])
            tempArray.push(tempData[0]);
          }
          annotationDataSet = tempArray;
        }
        annotationDataSet.forEach(annData => {
          if (segAnn.fieldMappings.hasOwnProperty('derivedNames')) {
            const derivedMapping = segAnn.fieldMappings.derivedNames;
            const derivedItem = derivedMapping.filter(derivedItem => {
              if (derivedItem.hasOwnProperty(segAnn.fieldMappings['name'])) {
                if (derivedItem[segAnn.fieldMappings['name']] === get(annData[segAnn.fieldMappings['name']], 'code')) {
                  return derivedItem;
                }
              }
            });

            seg.forEach(segment => {
              if (derivedItem[0] && derivedItem[0].values.indexOf(segment.name) > -1) {
                set(segment, 'resected', true);
              }
            });

          } else {
            seg.forEach(segment => {
              if(this.matchSegmentData(segment,annData)){
                segment.active = true;

                segment.level['code'] = get(annData[segAnn.fieldMappings['level']], 'code');

                /**
                 This is case when there no attribute for color coding, only selection attribute**/

                if (isEmpty(get(segment, 'level.code')) && segment.active) {
                  set(segment, 'level.code', 'finding');
                }
                set(segment, 'annotator', { type: segAnn.annotator, level: { code: get(segment, 'level.code')}});

                this.colorCodingDerivation.getColorCoding(this.segmentDefinition.legendCategories, segment,annData);

              }

            });
          }
        });
      });
      this.segments = seg;
      this.segments$.next(this.segments);
    }
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  /**
   * This method is used for comparision between the segment annotation property and the segment identifier properties
   */
  matchSegmentData(segment,annData):boolean{
    this.setSegmentIdentifier();
    let baseModelField = this.fieldMappingTransform.getModelFieldName(this.segmentDefinition.segmentAnnotations[0].fieldMappings);
    const mappingObject = this.fieldMappingTransform.segmentToModelMapper(this.segmentDefinition.segmentAnnotations[0]);
    let validDataArray:Array<boolean> = [];
    this.segmentIdentifier.forEach(identifier=>{
      let field = mappingObject[identifier];
        if (segment[identifier] == this.fieldMappingTransform.getCode(annData,field,this.segmentIdentifier,this.segmentDefinition.segmentAnnotations[0].fieldMappings)) {
            validDataArray.push(true);
          } else {
            validDataArray.push(false);
          }
    });

      if(validDataArray.includes(false)){
        return false;
      }else{
        return true
      }


  }

  /**
   * This method is used for comparision between the segment removed property and the segment identifier properties
   */
  checkSegmentRemoved(segment,fieldMappings):boolean{
    let baseModelField = this.fieldMappingTransform.getModelFieldName(this.segmentDefinition.segmentAnnotations[0].fieldMappings);
    this.setSegmentIdentifier();
    const mappingObject = this.fieldMappingTransform.segmentToModelMapper(this.segmentDefinition.segmentAnnotations[0]);
    let validDataArray:Array<boolean> = [];
    this.segmentIdentifier.forEach(identifier=>{
      let field = mappingObject[identifier];
        if (segment[identifier] === this.fieldMappingTransform.getCode(this.segmentRemoved,field,this.segmentIdentifier,this.segmentDefinition.segmentAnnotations[0].fieldMappings)) {
          validDataArray.push(true);
        } else {
          validDataArray.push(false);
        }
    });

    if(validDataArray.includes(false)){
      return false;
    }else{
      return true
    }
  }

  ngOnDestroy(): void {
    this.segmentsService.dismissObservable(this.payload.findingsObservableName);
    this.segmentsService.dismissObservable(this.payload.segmentsObservableName);
    this.segmentsService.dismissObservable(connectorObservable);
    this.destroyer$.next();
    this.destroyer$.complete();
  }



}
